import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { GameState, Room, Player, Round } from '../types';
import { useAuth } from './AuthContext';

interface GameContextType extends GameState {
  dispatch: React.Dispatch<GameAction>;
}

type GameAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ROOM'; payload: Room | null }
  | { type: 'SET_PLAYERS'; payload: Player[] }
  | { type: 'SET_CURRENT_ROUND'; payload: Round | null }
  | { type: 'SET_MY_PLAYER_ID'; payload: string | null }
  | { type: 'SET_PRESENCE'; payload: Record<string, boolean> }
  | { type: 'RECALCULATE_HOST' }
  | { type: 'RESET_GAME' };

// Internal reducer state adds a presence map derived from RTDB. RTDB is the
// single source of truth for the `connected` flag; the Firestore players
// snapshot must never overwrite it (that caused online players to flicker
// to "offline").
interface InternalGameState extends GameState {
  presenceMap: Record<string, boolean>;
}

const initialState: InternalGameState = {
  room: null,
  players: [],
  currentRound: null,
  myPlayerId: null,
  isHost: false,
  loading: false,
  error: null,
  presenceMap: {}
};

/** Apply the authoritative RTDB presence map onto a list of players. */
function applyPresence(players: Player[], presenceMap: Record<string, boolean>): Player[] {
  return players.map(player => ({
    ...player,
    connected: presenceMap[player.id] ?? player.connected
  }));
}

function gameReducer(state: InternalGameState, action: GameAction): InternalGameState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_ROOM':
      const isHost = action.payload && state.myPlayerId ? action.payload.hostId === state.myPlayerId : false;
      return { 
        ...state, 
        room: action.payload,
        isHost
      };
    
    case 'SET_PLAYERS':
      return { ...state, players: applyPresence(action.payload, state.presenceMap) };

    case 'SET_PRESENCE': {
      const presenceMap = { ...state.presenceMap, ...action.payload };
      return {
        ...state,
        presenceMap,
        players: applyPresence(state.players, presenceMap)
      };
    }

    case 'SET_CURRENT_ROUND':
      return { ...state, currentRound: action.payload };
    
    case 'SET_MY_PLAYER_ID':
      const isHostAfterSetId = state.room && action.payload ? state.room.hostId === action.payload : false;
      return { 
        ...state, 
        myPlayerId: action.payload,
        isHost: isHostAfterSetId
      };
    
    case 'RECALCULATE_HOST':
      const recalculatedIsHost = state.room && state.myPlayerId ? state.room.hostId === state.myPlayerId : false;
      return {
        ...state,
        isHost: recalculatedIsHost
      };
    
    case 'RESET_GAME':
      console.log('🔄 DEBUG: RESET_GAME - Clearing all game state');
      return initialState;
    
    default:
      return state;
  }
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const useGame = () => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

interface GameProviderProps {
  children: React.ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  const { user, authReady } = useAuth();

  // Set player ID when user is authenticated AND auth is ready
  useEffect(() => {
    // Only proceed if auth is ready
    if (!authReady) {
      return;
    }
    
    if (user?.uid) {
      dispatch({ type: 'SET_MY_PLAYER_ID', payload: user.uid });
    } else {
      dispatch({ type: 'SET_MY_PLAYER_ID', payload: null });
    }
  }, [user?.uid, authReady, state.myPlayerId]);

  // Recalculate host status when room or player ID changes
  useEffect(() => {
    if (state.room && state.myPlayerId) {
      dispatch({ type: 'RECALCULATE_HOST' });
    }
  }, [state.room?.hostId, state.myPlayerId]);

  const value: GameContextType = {
    ...state,
    dispatch
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};